<template>
  <DSFeatureHintSpotlight
    v-if="props.spotlight"
    ref="spotlightRef"
    :anchor-element="anchorElement"
    @click="dismiss(DS_FEATURE_HINT_DISMISS_REASON.Close)"
  />

  <DSPopover
    v-model="popoverOpen"
    :activator-element="anchorElement"
    :placement="placement"
    :offset="POPOVER_OFFSET"
    :close-on-click-outside="false"
    :constrain-to-viewport="false"
    with-arrow
    @escape="dismiss(DS_FEATURE_HINT_DISMISS_REASON.Close)"
    @positioned="spotlightRef?.update()"
  >
    <template #content>
      <DSFeatureHintCard
        ref="cardRef"
        v-bind="cardProps"
        @dismiss="dismiss"
      >
        <slot />
      </DSFeatureHintCard>
    </template>
  </DSPopover>
</template>

<script setup lang="ts">
import { computed, nextTick, onMounted, ref, watch } from "vue";
import DSPopover from "@/src/design_system/primitives/popover/components/DSPopover.vue";
import { DS_POPOVER_PLACEMENTS, type DSPopoverPlacement } from "@/src/design_system/primitives/popover/types/props";
import DSFeatureHintCard from "@/src/design_system/feedback/feature_hint/components/DSFeatureHintCard.vue";
import DSFeatureHintSpotlight from "@/src/design_system/feedback/feature_hint/components/DSFeatureHintSpotlight.vue";
import type { DSFeatureHintProps } from "@/src/design_system/feedback/feature_hint/types/props";
import type { DSFeatureHintCardProps } from "@/src/design_system/feedback/feature_hint/types/cardProps";
import {
  DS_FEATURE_HINT_DISMISS_REASON,
  type DSFeatureHintDismissReason,
  type DSFeatureHintEmits,
} from "@/src/design_system/feedback/feature_hint/types/emits";
import { isUserTyping } from "@/src/design_system/feedback/feature_hint/utils/focus";
import { useFeatureHintAnchor } from "@/src/design_system/feedback/feature_hint/composables/useFeatureHintAnchor";

const POPOVER_OFFSET = 12;

const props = withDefaults(defineProps<DSFeatureHintProps>(), {
  text: undefined,
  media: undefined,
  mediaAlt: undefined,
  learnMoreUrl: undefined,
  placement: DS_POPOVER_PLACEMENTS.BottomStart,
  badge: undefined,
  spotlight: false,
});

const emit = defineEmits<DSFeatureHintEmits>();
const isOpen = defineModel<boolean>("open", { default: false });

const spotlightRef = ref<InstanceType<typeof DSFeatureHintSpotlight>>();
const cardRef = ref<InstanceType<typeof DSFeatureHintCard>>();
// The card opens only after the anchor is resolved and the spotlight (if any) is shown, so it stacks above it.
const popoverOpen = ref(false);

const cardProps = computed<DSFeatureHintCardProps>(() => ({
  hintKey: props.hintKey,
  title: props.title,
  text: props.text,
  media: props.media,
  mediaAlt: props.mediaAlt,
  learnMoreUrl: props.learnMoreUrl,
  badge: props.badge,
}));

const placement = computed<DSPopoverPlacement>(() =>
  Object.values(DS_POPOVER_PLACEMENTS).includes(props.placement) ? props.placement : DS_POPOVER_PLACEMENTS.BottomStart
);

const { anchorElement, acquire, release } = useFeatureHintAnchor({
  hintKey: () => props.hintKey,
  onActivate: () => dismiss(DS_FEATURE_HINT_DISMISS_REASON.Anchor),
});

const dismiss = (reason: DSFeatureHintDismissReason) => {
  if (!isOpen.value) return;

  isOpen.value = false;
  emit("dismiss", reason);
  if (!isUserTyping()) anchorElement.value?.focus({ preventScroll: true });
};

const handleOpenChange = async (open: boolean) => {
  if (!open) {
    popoverOpen.value = false;
    spotlightRef.value?.hide();
    release();
    return;
  }

  if (!acquire()) {
    isOpen.value = false;
    emit("anchor-missing");
    return;
  }

  spotlightRef.value?.show();
  spotlightRef.value?.update();
  popoverOpen.value = true;
  await nextTick();
  if (!isUserTyping()) cardRef.value?.focus({ preventScroll: true });
};

watch(isOpen, handleOpenChange);

// A hint opened from the start resolves its anchor once mounted, when sibling anchors are in the document too.
onMounted(() => {
  if (isOpen.value) void handleOpenChange(true);
});
</script>
